import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ShieldCheck, Lock, Upload, FileText, Loader2, Gavel } from "lucide-react";
import { DOC_TYPES } from "../lib/claimFormPages";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const GOLD = "#C9A84C";

function OtpBox({ token, onSession, busy, setBusy }) {
  const [sent, setSent] = useState(null);
  const [otp, setOtp] = useState("");
  const start = async () => {
    setBusy(true);
    try { const r = await axios.post(`${API}/claim-form/${token}/otp/start`); setSent(r.data); toast.success(`OTP sent to ${r.data.sent_to}`); }
    catch (e) { toast.error(e.response?.data?.detail?.reason || e.response?.data?.detail || "Could not send OTP"); }
    finally { setBusy(false); }
  };
  const verify = async () => {
    setBusy(true);
    try { const r = await axios.post(`${API}/claim-form/${token}/otp/verify`, { code: otp }); onSession(r.data.session); toast.success("Identity re-verified — upload unlocked"); }
    catch (e) { toast.error(e.response?.data?.detail?.code === "invalid" ? `Incorrect code · ${e.response.data.detail.attempts_left} attempts left` : (e.response?.data?.detail?.code || "Verification failed")); }
    finally { setBusy(false); }
  };
  return (
    <div className="mt-6 border-2 border-[#111] bg-white p-5" data-testid="ec-otp-box">
      <div className="font-mono text-[12px] uppercase tracking-[0.3em] text-[#8a6d1f]">Identity re-verification · Vinci email OTP</div>
      <p className="text-[14px] text-[#444] mt-2">The sealed link has been re-opened for evidence only. Re-establish your identity before furnishing documents.</p>
      <div className="mt-3 flex flex-wrap items-center gap-3">
        <button data-testid="ec-otp-send" disabled={busy} onClick={start} className="px-4 py-2.5 bg-[#111] text-[#f3efe4] text-[13px] font-semibold uppercase tracking-wide disabled:opacity-50">{sent ? "Resend OTP" : "Send OTP"}</button>
        {sent && <>
          <input data-testid="ec-otp-input" value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="6-digit code" className="border border-[#2a2a2a] bg-[#faf8f2] px-3 py-2.5 font-mono text-[16px] tracking-[0.4em] w-44" />
          <button data-testid="ec-otp-verify" disabled={busy || otp.length < 4} onClick={verify} className="px-4 py-2.5 text-[13px] font-semibold uppercase tracking-wide disabled:opacity-50" style={{ background: GOLD }}>Verify</button>
          <span className="text-[13px] text-[#555]">Sent to {sent.sent_to} · valid {sent.ttl_minutes} min</span>
        </>}
      </div>
    </div>
  );
}

export const ClaimEvidenceCall = ({ token, meta, setMeta, onFurnished }) => {
  const storeKey = `vinci.claimform.${token}.session`;
  const [session, setSessionState] = useState(() => sessionStorage.getItem(storeKey) || "");
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");
  const call = meta.evidence_call;
  const setSession = (s) => { setSessionState(s); sessionStorage.setItem(storeKey, s); };
  const added = (meta.documents || []).filter((d) => d.evidence_call_id === call.id);
  const outstanding = call.slots.filter((s) => !added.some((d) => d.doc_type === s));
  const headers = { headers: { "X-Claim-Session": session } };

  const upload = async (docType, file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("doc_type", docType); fd.append("file", file);
    setBusy(true);
    try { const r = await axios.post(`${API}/claim-form/${token}/upload`, fd, headers); setMeta((m) => ({ ...m, documents: [...(m.documents || []), r.data.document] })); toast.success(`${file.name} placed on record`); }
    catch (e) { if (e.response?.status === 401) { setSession(""); } toast.error(e.response?.data?.detail?.message || e.response?.data?.detail || "Upload failed"); }
    finally { setBusy(false); }
  };
  const furnish = async () => {
    setBusy(true);
    try { const r = await axios.post(`${API}/claim-form/${token}/evidence-call/furnish`, { note }, headers); sessionStorage.removeItem(storeKey); onFurnished(r.data.evidence_call); }
    catch (e) { const d = e.response?.data?.detail; toast.error(d?.message || d?.code || d || "Could not confirm"); }
    finally { setBusy(false); }
  };

  return (
    <main className="max-w-5xl mx-auto px-4 md:px-6 py-8" data-testid="evidence-call-view">
      <section className="relative bg-[#f3efe4] shadow-2xl border border-[#c9bfa3] px-6 md:px-14 py-10">
        <div className="flex items-start justify-between gap-4 border-b-2 border-[#111] pb-4">
          <div>
            <div className="text-[12px] font-mono uppercase tracking-[0.3em] text-[#a3261f]">Evidence Call Notice · Part 9 re-opened under seal</div>
            <h2 className="font-serif text-2xl md:text-3xl mt-1">Additional documents called for</h2>
            <p className="text-[14px] text-[#444] mt-2 max-w-2xl">Form VNC/ICC-11 for this claim stands lodged. Parts 1–8, 10 and 11 remain sealed and cannot be amended. Only the Supporting Documents register is open, for the slots specified below.</p>
          </div>
          <div className="text-right text-[12px] font-mono text-[#555] shrink-0"><div>FORM VNC/ICC-11</div><div>NOTICE {call.id.toUpperCase()}</div><div>REF {meta.claim_ref}</div></div>
        </div>

        <div className="mt-6 border border-[#a3261f]/50 bg-[#fdeeec] p-5 text-[14px]" data-testid="ec-notice">
          <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#a3261f] inline-flex items-center gap-2"><Gavel size={13} /> Grounds stated by the Trade Desk</div>
          <p className="mt-2 text-[#5a1a14]" data-testid="ec-reason">{call.reason}</p>
          <p className="mt-2 text-[12px] text-[#7a1c16]">Issued {String(call.called_at).slice(0, 16).replace("T", " ")} UTC · Scrutiny is held in abeyance until the called-for evidence is furnished.</p>
        </div>

        {!session && <OtpBox token={token} onSession={setSession} busy={busy} setBusy={setBusy} />}

        <div className="mt-8 space-y-3" data-testid="ec-upload-section">
          <div className="font-mono text-[12px] uppercase tracking-[0.3em] text-[#8a6d1f]">Documents called for · {outstanding.length} outstanding</div>
          {DOC_TYPES.map((d) => {
            const called = call.slots.includes(d.key);
            const mine = added.filter((x) => x.doc_type === d.key);
            return (
              <div key={d.key} className={`flex flex-wrap items-center justify-between gap-3 border px-4 py-3 ${called ? "border-[#a3261f] bg-white" : "border-[#c9bfa3] bg-[#faf8f2] opacity-60"}`} data-testid={`ec-doc-${d.key}`} data-called={called} data-furnished={mine.length > 0}>
                <div className="text-[14px]">
                  <span className={called ? "font-semibold" : ""}>{d.label}</span>
                  {called && <span className="ml-2 font-mono text-[10px] tracking-[0.2em]" style={{ color: mine.length ? "#1d7a5f" : "#a3261f" }}>{mine.length ? "FURNISHED" : "CALLED FOR"}</span>}
                  {mine.map((x) => <div key={x.id} className="text-[12px] text-[#555] inline-flex items-center gap-1 mt-1 mr-3"><FileText size={12} />{x.filename}</div>)}
                </div>
                <label className={`inline-flex items-center gap-2 px-3 py-2 text-[13px] font-semibold uppercase tracking-wide border ${session ? "border-[#111] cursor-pointer hover:bg-[#111] hover:text-[#f3efe4]" : "border-[#999] text-[#999]"}`}>
                  <Upload size={14} /> Upload<input type="file" data-testid={`ec-upload-${d.key}`} className="hidden" disabled={!session || busy} accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => upload(d.key, e.target.files?.[0])} />
                </label>
              </div>
            );
          })}
        </div>

        <div className="mt-8 border-t-2 border-[#111] pt-5">
          <label className="block text-[13px] font-semibold uppercase tracking-wide text-[#333]">Covering note to the Trade Desk (optional)</label>
          <textarea data-testid="ec-note" rows={2} value={note} onChange={(e) => setNote(e.target.value)} className="w-full mt-2 border border-[#2a2a2a] bg-[#faf8f2] px-3 py-2.5 text-[15px] outline-none focus:border-[#C9A84C]" />
          <div className="mt-4 flex items-center justify-between gap-4 flex-wrap">
            <p className="text-[12px] text-[#555] max-w-xl">By confirming, you attest that the documents furnished are true copies of originals held by the claimant entity and relate solely to the order under claim.</p>
            <button data-testid="ec-furnish" disabled={!session || busy || outstanding.length > 0} onClick={furnish} title={!session ? "Re-verify identity first" : outstanding.length ? "Upload every called-for document" : ""} className="inline-flex items-center gap-2 px-6 py-3 text-[13px] font-bold uppercase tracking-wide disabled:opacity-40" style={{ background: GOLD }}>
              {busy ? <Loader2 size={14} className="animate-spin" /> : session ? <ShieldCheck size={14} /> : <Lock size={14} />} Furnish evidence & confirm
            </button>
          </div>
        </div>
      </section>
    </main>
  );
};
